import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  TemplateRef,
  computed,
  contentChild,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { NgTemplateOutlet, isPlatformServer } from '@angular/common';
import { IconGeolocationComponent, IconLoadingComponent } from '@pedix-workspace/angular-ui-icons';

import type * as L from 'leaflet';
import { GeolocationService } from '../../geolocation.service';
import { StadiaMapsLoaderService } from '../stadia-maps-loader.service';
import { StadiaMapsContextService } from '../stadia-maps-context.service';
import { StadiaLatLng } from '../stadia-maps.types';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, filter } from 'rxjs';
import { StadiaMapsPopupService } from '../stadia-maps-popup.service';
import { StadiaMapsService } from '../stadia-maps.service';

@Component({
  selector: 'pxw-stadia-maps',
  imports: [NgTemplateOutlet, IconGeolocationComponent, IconLoadingComponent],
  templateUrl: './stadia-maps.component.html',
  styleUrl: './stadia-maps.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [StadiaMapsContextService, StadiaMapsPopupService],
})
export class StadiaMapsComponent implements AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private renderer = inject(Renderer2);
  private stadiaMapsLoaderService = inject(StadiaMapsLoaderService);
  private stadiaMapsPopupService = inject(StadiaMapsPopupService);
  private stadiaMapsContext = inject(StadiaMapsContextService);
  private geolocationService = inject(GeolocationService);
  private stadiaMapsService = inject(StadiaMapsService);

  mapRef = viewChild.required<ElementRef<HTMLElement>>('mapRef');
  mapContents = contentChild<TemplateRef<unknown>>('mapContents');

  readonly height = input<number>(300);
  readonly center = input<StadiaLatLng>({ lat: 0, lng: 0 });
  readonly zoom = input<number>(3);
  readonly displayMyLocationButton = input<boolean>(false);
  readonly geolocationErrorText = input.required<string>();

  readonly isMapReady = signal(false);
  readonly loadingGeolocation = signal(false);

  readonly mapElement = computed(() => this.mapRef().nativeElement);

  readonly mapLoaded = output<void>();
  readonly mapClick = output<StadiaLatLng>();
  readonly geolocationResult = output<StadiaLatLng>();

  private L: typeof import('leaflet') | null = null;
  private isDestroyed = false;

  mapInstance: L.Map | null = null;

  @HostBinding('style.height.px')
  get styleHeight() {
    return this.height();
  }

  constructor() {
    combineLatest([
      toObservable(this.isMapReady),
      toObservable(this.center),
      toObservable(this.zoom),
    ])
      .pipe(
        filter(([isMapReady, center, zoom]) => Boolean(isMapReady && center && zoom)),
        takeUntilDestroyed(),
      )
      .subscribe(([isMapReady, center, zoom]) => {
        this.mapInstance?.flyTo([center.lat, center.lng], zoom);
      });
  }

  async ngAfterViewInit() {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    await this.stadiaMapsLoaderService.loadResources(this.renderer);

    this.L = await this.stadiaMapsLoaderService.getLeaflet();

    if (this.isDestroyed) {
      return;
    }

    this.mapInstance = this.L.map(this.mapElement(), {
      center: [this.center().lat, this.center().lng],
      zoom: this.zoom(),
      attributionControl: false,
      zoomControl: false,
    });

    this.L.tileLayer(this.stadiaMapsService.getTilesUrl(), {
      maxZoom: 20,
      attribution:
        '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.mapInstance);

    this.L.control.zoom({ position: 'bottomright' }).addTo(this.mapInstance);

    this.mapInstance.on('click', (event: L.LeafletMouseEvent) => {
      const coordinates: StadiaLatLng = {
        lat: event.latlng.lat,
        lng: event.latlng.lng,
      };

      this.stadiaMapsPopupService.removeAllPopupComponents();

      this.mapClick.emit(coordinates);
    });

    this.mapInstance.whenReady(() => {
      this.isMapReady.set(true);

      this.mapLoaded.emit();
    });

    this.stadiaMapsContext.mapInstance = this.mapInstance;
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;

    if (this.mapInstance) {
      this.mapInstance.remove();
    }
  }

  fitBoundsToMarkers(
    markers: StadiaLatLng[],
    options?: {
      padding?: { top: number; bottom: number; left: number; right: number };
      maxZoom?: number;
      duration?: number;
    },
  ): void {
    if (!markers.length || !this.mapInstance || !this.isMapReady() || !this.L) {
      return;
    }

    const latLngs: L.LatLngExpression[] = markers.map(m => [m.lat, m.lng] as [number, number]);
    const bounds = this.L.latLngBounds(latLngs);

    const padding = options?.padding || { top: 50, bottom: 50, left: 50, right: 50 };

    this.mapInstance.fitBounds(bounds, {
      paddingTopLeft: [padding.left, padding.top],
      paddingBottomRight: [padding.right, padding.bottom],
      maxZoom: options?.maxZoom || 15,
      duration: options?.duration,
      animate: true,
    });
  }

  protected async onGeolocateUser() {
    this.loadingGeolocation.set(true);

    try {
      const geolocationResult = await this.geolocationService
        .geolocate()
        .then(geolocationResult => {
          if (!geolocationResult) {
            return Promise.reject();
          }
          return geolocationResult;
        });
      this.geolocationResult.emit(geolocationResult);
    } catch (error) {
      if (error) {
        console.error(error);
      }
      alert(this.geolocationErrorText());
    }

    this.loadingGeolocation.set(false);
  }
}
